import {
  Component,
  Input,
  Injectable,
  Inject,
  LOCALE_ID,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NgbDateStruct, NgbDate, NgbDateParserFormatter, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import 'moment/locale/nl';
import { LangChangeEvent, TranslateService, TranslatePipe } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { FeatherModule } from 'angular-feather';

import { ValueComponent } from '~/v2/core/valuecomponent';
import { LocaleService } from '~/v2/core/services/locale.service';
import { LocalDatePipe } from '../../pipes/local-date.pipe';

@Injectable()
export class CustomDateParserFormatter extends NgbDateParserFormatter {
  constructor(@Inject(LOCALE_ID) private locale: string) {
    super();
  }

  parse(value: string): NgbDateStruct | null {
    if (!value) {
      return null;
    }

    const momentDate = moment(value, 'L', this.locale);
    if (!momentDate.isValid()) {
      return null;
    }

    return {
      year: momentDate.year(),
      month: momentDate.month() + 1,
      day: momentDate.date(),
    };
  }

  format(date: NgbDateStruct | null): string {
    if (!date) {
      return '';
    }

    const mdt = moment([date.year, date.month - 1, date.day]);
    if (!mdt.isValid()) {
      return '';
    }

    const locale = this.locale.toString();
    mdt.locale(locale);
    return mdt.format('L');
  }
}

@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  imports: [NgbInputDatepicker, FormsModule, NgClass, FeatherModule, TranslatePipe, LocalDatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [{ provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }],
})
export class DatePickerComponent extends ValueComponent<any> implements OnChanges, OnInit {
  @Input() title = '';
  @Input() minDate: Date = new Date(1900, 0, 1, 0, 0, 0, 0);
  @Input() maxDate: Date = new Date(this.GetDefaultMaxYear(), 0, 1, 0, 0, 0, 0);
  @Input() isReadOnly = false;
  @Input() disabledDates: string[] | null = null;
  @Output() dateChanged = new EventEmitter<any>();

  ngbDateValue: NgbDateStruct | null = null;
  dateDisplay: Date | null = null;
  disabled = false;
  localeSettings: any;
  private localeService: LocaleService;
  private translateService: TranslateService;
  private localeSettingsMap = new Map<string, object>(
    [
      {
        locale: 'nl',
        settings: {
          placeholder: 'DD-MM-JJJJ',
          validQuarterOneToThreeDate: '01-01-2019',
          validQuarterFourDate: '01-10-2019',
          separator: '-',
        },
      },
      {
        locale: 'en',
        settings: {
          placeholder: 'MM/DD/YYYY',
          validQuarterOneToThreeDate: '01/01/2019',
          validQuarterFourDate: '10/01/2019',
          separator: '/',
        },
      },
    ].map((x) => [x.locale, x.settings] as [string, object]),
  );

  constructor(@Inject(LOCALE_ID) locale: string, localeService: LocaleService, translateService: TranslateService) {
    super();
    this.localeService = localeService;
    this.translateService = translateService;
    this.setLocaleSettings(locale.toString());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes == undefined || changes['value'] == undefined) {
      return;
    }

    this.value = changes['value'].currentValue;
    this.initNgbDateValue(this.value);
  }

  ngOnInit(): void {
    if (this.value) {
      this.initNgbDateValue(this.value);
    }
    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.setLocaleSettings(event.lang);
    });
  }

  getMinDate(): NgbDateStruct {
    return this.getNgbDateStruct(this.minDate);
  }

  getMaxDate(): NgbDateStruct {
    return this.getNgbDateStruct(this.maxDate);
  }

  initNgbDateValue(dateString: any): void {
    if (dateString == null) {
      this.dateDisplay = null;
      this.ngbDateValue = null;
      return;
    }

    const localDate = moment(dateString).toDate();

    const day = Number(localDate.getDate());
    const month = Number(localDate.getMonth() + 1);
    const year = Number(localDate.getFullYear());
    this.setDisplayDate(day, month, year);
    this.ngbDateValue = { year: year, month: month, day: day };
  }

  onPaste(event: any): void {
    event.preventDefault();
  }

  onDragOver(event: any): any {
    event.preventDefault();
  }

  focusOut(): void {
    const isValid = this.isValid();

    if (!isValid) {
      this.value = null;
      this.valueChange.emit(this.value);
      this.isValid();
    }
  }

  onKeyPress(event: any): void {
    const value = event.target.value;
    const pressedKeyIsNotANumber = event.charCode >= 48 && event.charCode <= 57;

    const startNewString = event.target.selectionStart;
    const endNewString = event.target.selectionEnd;
    const selectedChars = endNewString - startNewString;

    if (!pressedKeyIsNotANumber || value.length - selectedChars > 9) {
      event.preventDefault();
      return;
    }

    let isValid = false;
    const newValue = value.slice(0, startNewString) + event.key + value.slice(endNewString);
    const validDate = this.getValidDate(newValue);

    if (newValue.length <= validDate.length) {
      const dateToValidate = newValue + validDate.substr(newValue.length);
      isValid = moment(dateToValidate, this.localeService.getLongDateFormat(), true).isValid();
    }

    if (!isValid) {
      event.preventDefault();
      return;
    }

    if (newValue.length == 2 || newValue.length == 5) {
      event.target.value = newValue + this.localeSettings.separator;
      event.preventDefault();
    }

    if (newValue.length > 10) {
      event.preventDefault();
    }

    if (newValue.length == 10) {
      event.preventDefault();
    }

    const fullDateEntered: boolean = newValue.length == validDate.length;

    if (fullDateEntered) {
      const momentDate = moment(newValue, this.localeService.getLongDateFormat(), true);
      this.value = momentDate.format('YYYY-MM-DD');

      this.initNgbDateValue(this.value);
      this.valueChange.emit(this.value);
    }
  }

  shouldMarkDisabled(date: NgbDate): boolean {
    if (!this.disabledDates) {
      return false;
    }

    const dateString = this.getDateString(date);
    return this.disabledDates.indexOf(dateString) > -1;
  }

  markDisabled = (date: NgbDate): boolean => {
    const shouldMarkDisabled = this.shouldMarkDisabled(date);
    return shouldMarkDisabled;
  };

  override isValid(): boolean {
    let isValid = super.isValid();
    if (isValid) {
      isValid = this.isDateValid(this.ngbDateValue!);
    }
    return isValid;
  }

  isEmpty(): boolean {
    return !this.isDateValid(this.ngbDateValue!);
  }

  isDateValid(date: NgbDateStruct | string): boolean {
    if (date == null) {
      return false;
    }

    let m: moment.Moment;

    if (typeof date === 'string') {
      m = moment(date, this.localeService.getLongDateFormat(), true);
    } else {
      m = moment([date.year, date.month - 1, date.day]);
    }

    if (!m.isValid()) {
      return false;
    }

    // Verify that date is between min and max dates (if user manually writes/adjusts a date,
    // this is not picked up by ngb datepicker's min- and maxdate validators: https://github.com/angular-ui/bootstrap/issues/6124)
    return m.isSameOrAfter(moment(this.minDate), 'day') && m.isSameOrBefore(moment(this.maxDate), 'day');
  }

  onDateSelection(date: NgbDate): void {
    this.updateStringValue(date);
    this.setDisplayDateFromDate(date);
    this.isValid();
  }

  private GetDefaultMaxYear(): number {
    const defaultMaxYearsInFuture = 280;
    const date = new Date();
    return date.getFullYear() + defaultMaxYearsInFuture;
  }

  private getNgbDateStruct(date: Date): NgbDateStruct {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  private updateStringValue(date: NgbDate): void {
    this.value = this.getDateString(date);
    this.valueChange.emit(this.value);
  }

  private getDateString(date: any): string {
    const month = String(date.month).padStart(2, '0');
    const day = String(date.day).padStart(2, '0');

    return `${date.year}-${month}-${day}`;
  }

  private getValidDate(datePart: string): string {
    let firstMontDigit = '0';

    if (this.localeSettings.locale == 'nl' && datePart.length > 3) {
      firstMontDigit = datePart.charAt(3);
    } else if (this.localeSettings.locale == 'en') {
      firstMontDigit = datePart.charAt(0);
    }

    const monthOfLastQuarterSelected = firstMontDigit == '1';

    return monthOfLastQuarterSelected
      ? this.localeSettings.validQuarterFourDate
      : this.localeSettings.validQuarterOneToThreeDate;
  }

  private setDisplayDateFromDate(date: NgbDate): void {
    this.setDisplayDate(date.day, date.month, date.year);
  }

  private setDisplayDate(day: number, month: number, year: number): void {
    this.dateDisplay = new Date();
    this.dateDisplay.setFullYear(year, month - 1, day);

    this.dateChanged.emit(this.dateDisplay);
  }

  private setLocaleSettings(locale: string): void {
    this.localeSettings = this.localeSettingsMap.get(locale);
  }
}
